import axios from 'axios';

// Create axios instance with base URL
// In development, API requests are proxied to backend port 8080 via the proxy field in package.json
// In production, requests go directly to the API_URL (e.g. api.internhubapp.com)

// Debug: Log the environment variables
console.log('Environment variables:', {
  REACT_APP_API_URL: process.env.REACT_APP_API_URL,
  NODE_ENV: process.env.NODE_ENV,
});

// Determine the base URL with proper fallback
const getBaseURL = () => {
  // If we have an explicit API URL, use it
  if (process.env.REACT_APP_API_URL) {
    console.log('Using configured API URL:', process.env.REACT_APP_API_URL);
    return process.env.REACT_APP_API_URL;
  }

  // Fallback logic
  if (process.env.NODE_ENV === 'production') {
    console.log('Production fallback: using api.internhubapp.com');
    return 'https://api.internhubapp.com';
  } else {
    console.log('Development fallback: using localhost:8000');
    return 'http://localhost:8000';
  }
};

const baseURL = getBaseURL();
console.log('Final axios baseURL:', baseURL);

const instance = axios.create({
  baseURL: baseURL,
  withCredentials: true, // Include cookies in requests
});

// Store reference to auth context (will be set by AuthProvider)
let authContext = null;

// Function to set auth context reference
export const setAuthContext = (context) => {
  authContext = context;
};

// Add request interceptor to include access token
instance.interceptors.request.use(
  (config) => {
    // Get auth state from context
    const accessToken = authContext?.accessToken;
    const isAuthenticated = authContext?.isAuthenticated;

    // Public endpoints that don't need authentication
    const publicEndpoints = [
      '/api/v1/auth/google/callback',
      '/api/v1/auth/refresh',
      '/api/v1/auth/register/init',
      '/api/v1/auth/register/info',
      '/api/v1/auth/register/complete',
      '/api/v1/auth/login',
      '/api/v1/auth/verify-email',
      '/api/v1/auth/forgot-password',
      '/api/v1/auth/reset-password',
      '/api/v1/university',
      '/api/v1/open/', // All open registration endpoints
      '/api/v1/config/', // Config endpoints (for Turnstile config)
    ];

    const isPublicEndpoint = publicEndpoints.some((endpoint) => config.url?.includes(endpoint));

    // If it's a protected endpoint and user is not authenticated, reject the request
    if (!isPublicEndpoint && !isAuthenticated) {
      console.log(
        'Axios interceptor: Blocking request to protected endpoint while not authenticated:',
        config.url
      );
      return Promise.reject(new Error('User not authenticated - request blocked'));
    }

    // Add authorization header for authenticated requests to protected endpoints
    if (accessToken && !isPublicEndpoint) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token refresh
instance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Don't attempt token refresh for:
    // 1. Requests that are already retries
    // 2. Requests to the refresh endpoint itself (to prevent infinite loops)
    // 3. When authContext is not available
    // 4. When user is not authenticated
    // 5. When user doesn't have an access token
    const isRefreshEndpoint = originalRequest.url?.includes('/auth/refresh');
    const shouldAttemptRefresh =
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isRefreshEndpoint &&
      authContext &&
      authContext.isAuthenticated && // Only attempt refresh if user is authenticated
      authContext.accessToken; // And has an access token

    if (shouldAttemptRefresh) {
      originalRequest._retry = true;

      try {
        console.log('Axios interceptor: Attempting token refresh');
        await authContext.refreshAccessToken();

        // Update the original request with new access token
        const newAccessToken = authContext.accessToken;
        if (newAccessToken) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        }

        console.log('Axios interceptor: Token refresh successful, retrying request');
        return instance(originalRequest);
      } catch (refreshError) {
        console.error('Axios interceptor: Token refresh failed', refreshError);

        // If refresh fails, logout user
        authContext.logout();

        // Redirect to login page if we're not already there
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }

        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default instance;
